import { forwardRef } from 'react';

import type { SocialProofTileProps } from '../SocialProofTile';
import { SocialProofColumn } from './SocialProofColumn';

export type SliderProps = {
  columns: SocialProofTileProps[][];
};

/**
 * With `loop: true`, keen-slider needs enough rendered content to always span more than
 * one container width, or it runs out of tiles to drag/autoplay into view before the loop
 * wraps back around — leaving a visible gap until the next tile snaps fully into place.
 * Repeating the columns keeps that buffer comfortable at every viewport width where the
 * slider (rather than the static grid) is shown.
 */
const REPEAT_COUNT = 3;

export const SocialProofSlider = forwardRef<HTMLDivElement, SliderProps>(({ columns }, ref) => (
  <div ref={ref} className="keen-slider overflow-visible cursor-grab active:cursor-grabbing">
    {Array.from({ length: REPEAT_COUNT }, (_, repeatIndex) =>
      columns.map((column, columnIndex) => (
        /**
         * The shrink-0 and !w-auto classes are necessary to prevent the slider from shrinking
         * the columns to fit the viewport width, which would break the layout of stacked tiles.
         * The !overflow-visible class is necessary to allow the shadow of the tiles to be visible outside the slider container.
         */
        <SocialProofColumn
          key={`${repeatIndex}-${columnIndex}`}
          column={column}
          className="keen-slider__slide shrink-0 !w-auto !overflow-visible"
        />
      ))
    )}
  </div>
));

SocialProofSlider.displayName = 'SocialProofSlider';
